import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import "./AdminDashboard.scss";
import useTranslation from "../../../hooks/useTranslation";
import Box from "@mui/material/Box";
import {
  Alert,
  CircularProgress,
  Tab,
  TablePagination,
  Tabs,
} from "@mui/material";
import CustomTabPanel from "../../../components/CustomTabPanel/CustomTabPanel";
import SearchBar from "../../../components/SearchBar";
import { User, UserOrderBy } from "../../../stores/AuthStore/types";
import { Case, CaseOrderBy } from "../../../stores/CaseStore/types";
import { Offer } from "../../../stores/OfferStore/types";
import authStore from "../../../stores/AuthStore";
import feedbackStore from "../../../stores/FeedbackStore";
import { FeedbackSeverity } from "../../../stores/FeedbackStore/types";
import UserItem from "./UserItem";
import CaseItem from "./CaseItem";
import OfferItem from "./OfferItem";
import caseStore from "../../../stores/CaseStore";
import offerStore from "../../../stores/OfferStore";
import configStore from "../../../stores/ConfigStore";

const AdminDashboard = observer(() => {
  const { t } = useTranslation();
  const [value, setValue] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [keywords, setKeywords] = useState({
    userKeyword: "",
    caseKeyword: "",
    offerKeyword: "",
  });
  const [loading, setLoading] = useState({
    usersLoading: false,
    casesLoading: false,
    offersLoading: false,
  });
  const [paging, setPaging] = useState({
    usersPaging: {
      page: 0,
      rowsPerPage: 50,
      rowsCount: 0,
    },
    casesPaging: {
      page: 0,
      rowsPerPage: 25,
      rowsCount: 0,
    },
    offersPaging: {
      page: 0,
      rowsPerPage: 50,
      rowsCount: 0,
    },
  });

  // Page effects
  useEffect(() => {
    if (value === 0) {
      searchUsers(keywords.userKeyword);
    }
  }, [value, paging.usersPaging.page, paging.usersPaging.rowsPerPage]);

  useEffect(() => {
    if (value === 1) {
      searchCases(keywords.caseKeyword);
    }
  }, [value, paging.casesPaging.page, paging.casesPaging.rowsPerPage]);

  useEffect(() => {
    if (value === 2) {
      searchOffers(keywords.offerKeyword);
    }
  }, [value, paging.offersPaging.page, paging.offersPaging.rowsPerPage]);

  // Search functions
  const searchUsers = (keyword: string) => {
    setLoading((prev) => ({
      ...prev,
      usersLoading: true,
    }));

    authStore
      .searchUsers(
        keyword,
        UserOrderBy.CREATE_DATE_DESC,
        paging.usersPaging.page,
        paging.usersPaging.rowsPerPage
      )
      .then((result) => {
        setUsers(result.users);
        setPaging((prev) => ({
          ...prev,
          usersPaging: {
            ...prev.usersPaging,
            rowsCount: result.count,
          },
        }));
      })
      .finally(() => {
        setLoading((prev) => ({
          ...prev,
          usersLoading: false,
        }));
      });
  };

  const searchCases = (keyword: string) => {
    setLoading((prev) => ({
      ...prev,
      casesLoading: true,
    }));

    caseStore.setSearchKeyword(keyword);
    caseStore
      .searchCases(
        CaseOrderBy.CREATE_DATE_DESC,
        paging.casesPaging.page,
        paging.casesPaging.rowsPerPage
      )
      .then((result) => {
        setCases(result.cases);
        setPaging((prev) => ({
          ...prev,
          casesPaging: {
            ...prev.casesPaging,
            rowsCount: result.count,
          },
        }));
      })
      .catch(() =>
        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.ERROR,
          message: "connection-error",
        })
      )
      .finally(() => {
        setLoading((prev) => ({
          ...prev,
          casesLoading: false,
        }));
      });
  };

  const searchOffers = (keyword: string) => {
    setLoading((prev) => ({
      ...prev,
      offersLoading: true,
    }));

    offerStore
      .searchOffers(keyword)
      .then((result) => {
        setOffers(result.offers);
        setPaging((prev) => ({
          ...prev,
          offersPaging: {
            ...prev.offersPaging,
            rowsCount: result.count,
          },
        }));
      })
      .catch(() =>
        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.ERROR,
          message: "connection-error",
        })
      )
      .finally(() => {
        setLoading((prev) => ({
          ...prev,
          offersLoading: false,
        }));
      });
  };

  // Handlers
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleOnSearchUser = (keyword: string) => {
    setKeywords((prev) => ({
      ...prev,
      userKeyword: keyword,
    }));

    if (paging.usersPaging.page !== 0) {
      setPaging((prev) => ({
        ...prev,
        usersPaging: {
          ...prev.usersPaging,
          page: 0,
        },
      }));
    } else {
      searchUsers(keyword);
    }
  };

  const handleOnSearchCase = (keyword: string) => {
    setKeywords((prev) => ({
      ...prev,
      caseKeyword: keyword,
    }));

    if (paging.casesPaging.page !== 0) {
      setPaging((prev) => ({
        ...prev,
        casesPaging: {
          ...prev.casesPaging,
          page: 0,
        },
      }));
    } else {
      searchCases(keyword);
    }
  };

  const handleOnSearchOffer = (keyword: string) => {
    setKeywords((prev) => ({
      ...prev,
      offerKeyword: keyword,
    }));

    if (paging.offersPaging.page !== 0) {
      setPaging((prev) => ({
        ...prev,
        offersPaging: {
          ...prev.offersPaging,
          page: 0,
        },
      }));
    } else {
      searchOffers(keyword);
    }
  };

  const afterUpdateUser = (user: User) => {
    setUsers((prev) =>
      prev.map(
        (prevUser): User =>
          prevUser.id === user.id ? { ...prevUser, ...user } : prevUser
      )
    );
  };

  const handleUsersChangePage = (event: unknown, newPage: number) => {
    setPaging((prev) => ({
      ...prev,
      usersPaging: {
        ...prev.usersPaging,
        page: newPage,
      },
    }));
  };

  const handleUsersChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPaging((prev) => ({
      ...prev,
      usersPaging: {
        ...prev.usersPaging,
        rowsPerPage: parseInt(event.target.value, 10),
        page: 0,
      },
    }));
  };

  const handleCasesChangePage = (event: unknown, newPage: number) => {
    setPaging((prev) => ({
      ...prev,
      casesPaging: {
        ...prev.casesPaging,
        page: newPage,
      },
    }));
  };

  const handleCasesChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPaging((prev) => ({
      ...prev,
      casesPaging: {
        ...prev.casesPaging,
        rowsPerPage: parseInt(event.target.value, 10),
        page: 0,
      },
    }));
  };

  const handleOffersChangePage = (event: unknown, newPage: number) => {
    setPaging((prev) => ({
      ...prev,
      offersPaging: {
        ...prev.offersPaging,
        page: newPage,
      },
    }));
  };

  const handleOffersChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPaging((prev) => ({
      ...prev,
      offersPaging: {
        ...prev.offersPaging,
        rowsPerPage: parseInt(event.target.value, 10),
        page: 0,
      },
    }));
  };

  const a11yProps = (index: number) => {
    return {
      id: `tab-${index}`,
      "aria-controls": `tabpanel-${index}`,
    };
  };

  return (
    <div className="adminDashboard">
      <Alert severity="info" icon={false}>
        Aktif oturum sayısı: {configStore.appConfig.sessionCount || 0}
      </Alert>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          variant={"fullWidth"}
          value={value}
          onChange={handleChange}
          aria-label="icon label tabs"
          centered
        >
          <Tab
            icon={<i className="fal fa-users" />}
            label={t("users")}
            {...a11yProps(0)}
          />
          <Tab
            icon={<i className="fal fa-file" />}
            label={t("user-cases")}
            {...a11yProps(1)}
          />
          <Tab
            icon={<i className="fal fa-hand-holding-usd" />}
            label={t("offers")}
            {...a11yProps(2)}
          />
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        <SearchBar handleOnSearch={handleOnSearchUser} />
        {loading.usersLoading ? (
          <div style={{ textAlign: "center" }}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className="tabContent">
              {users.length === 0 ? (
                <Alert severity={"warning"}>{t("no-result")}</Alert>
              ) : (
                users.map((user) => (
                  <UserItem
                    key={`admin-user-item-${user.id}`}
                    user={user}
                    afterUpdateUser={afterUpdateUser}
                  />
                ))
              )}
            </div>

            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={t("rows-per-page")}
              component="div"
              count={paging.usersPaging.rowsCount}
              rowsPerPage={paging.usersPaging.rowsPerPage}
              page={paging.usersPaging.page}
              onPageChange={handleUsersChangePage}
              onRowsPerPageChange={handleUsersChangeRowsPerPage}
              showFirstButton
              showLastButton
            />
          </>
        )}
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <SearchBar handleOnSearch={handleOnSearchCase} />
        {loading.casesLoading ? (
          <div style={{ textAlign: "center" }}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className="tabContent">
              {!cases || cases.length === 0 ? (
                <Alert severity={"warning"}>{t("no-result")}</Alert>
              ) : (
                cases.map((caseItem) => (
                  <CaseItem
                    key={`admin-case-item-${caseItem.id}`}
                    caseItem={caseItem}
                  />
                ))
              )}
            </div>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={t("rows-per-page")}
              component="div"
              count={paging.casesPaging.rowsCount}
              rowsPerPage={paging.casesPaging.rowsPerPage}
              page={paging.casesPaging.page}
              onPageChange={handleCasesChangePage}
              onRowsPerPageChange={handleCasesChangeRowsPerPage}
              showFirstButton
              showLastButton
            />
          </>
        )}
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
        <SearchBar handleOnSearch={handleOnSearchOffer} />
        {loading.offersLoading ? (
          <div style={{ textAlign: "center" }}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className="tabContent">
              {!offers || offers.length === 0 ? (
                <Alert severity={"warning"}>{t("no-result")}</Alert>
              ) : (
                offers.map((offer) => (
                  <OfferItem
                    key={`admin-offer-item-${offer.id}`}
                    offer={offer}
                  />
                ))
              )}
            </div>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={t("rows-per-page")}
              component="div"
              count={paging.offersPaging.rowsCount}
              rowsPerPage={paging.offersPaging.rowsPerPage}
              page={paging.offersPaging.page}
              onPageChange={handleOffersChangePage}
              onRowsPerPageChange={handleOffersChangeRowsPerPage}
              showFirstButton
              showLastButton
            />
          </>
        )}
      </CustomTabPanel>
    </div>
  );
});

export default AdminDashboard;
